import booleanIntersects from "@turf/boolean-intersects";
import { feature } from "topojson-client";

import fsaFileUrl from "../../../../../../../data/canada-fsa.topojson";

const ringsOf = (geometry) =>
  geometry.type === "MultiPolygon"
    ? geometry.coordinates.flat()
    : geometry.coordinates;

const boxOf = (rings) => {
  const box = { north: -90, south: 90, east: -180, west: 180 };
  for (const ring of rings) {
    for (const [lng, lat] of ring) {
      if (lat > box.north) box.north = lat;
      if (lat < box.south) box.south = lat;
      if (lng > box.east) box.east = lng;
      if (lng < box.west) box.west = lng;
    }
  }
  return box;
};

const overlaps = (a, b) =>
  a.west <= b.east &&
  b.west <= a.east &&
  a.south <= b.north &&
  b.south <= a.north;

const toArea = ({ properties, geometry }) => {
  const rings = ringsOf(geometry);
  return {
    code: properties.fsa,
    geometry,
    box: boxOf(rings),
    label: { lat: properties.ly, lng: properties.lx },
    paths: rings.map((ring) =>
      ring.slice(0, -1).map(([lng, lat]) => ({ lat, lng })),
    ),
  };
};

const EARTH_RADIUS_KM = 6371;
const PLACE_KIND_ORDER = { city: 0, metro: 1, province: 2 };

const normalize = (text) =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const countsByPrefix = (areas) => {
  const counts = new Map();
  areas.forEach(({ code }) =>
    [1, 2, 3].forEach((length) => {
      const key = code.slice(0, length);
      counts.set(key, (counts.get(key) || 0) + 1);
    }),
  );
  return counts;
};

const toPlace = (counts) => ([name, kind, province, prefixes]) => {
  const list = prefixes.split(" ");
  return {
    name,
    kind,
    province,
    prefixes: list,
    count: list.reduce((sum, prefix) => sum + (counts.get(prefix) || 0), 0),
    search: normalize(name),
  };
};

let loading = null;

export const loadPostalAreas = () => {
  if (!loading) {
    loading = fetch(fsaFileUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`Postal areas: ${res.status}`);
        return res.json();
      })
      .then((topology) => {
        const areas = feature(topology, topology.objects.fsa).features.map(
          toArea,
        );
        const places = (topology.places || []).map(
          toPlace(countsByPrefix(areas)),
        );
        return { areas, places };
      })
      .catch((error) => {
        loading = null;
        throw error;
      });
  }
  return loading;
};

export const areasFor = (areas, prefixes) =>
  areas.filter((area) =>
    prefixes.some((prefix) => area.code.startsWith(prefix)),
  );

const matchRank = (name, query) => {
  if (name === query) return 0;
  if (name.startsWith(query)) return 1;
  if (name.includes(` ${query}`)) return 2;
  return null;
};

export const searchPlaces = (places, text, limit) => {
  const query = normalize(text);
  if (query.length < 2) return [];
  return places
    .map((place) => ({ place, rank: matchRank(place.search, query) }))
    .filter(({ rank }) => rank !== null)
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        PLACE_KIND_ORDER[a.place.kind] - PLACE_KIND_ORDER[b.place.kind] ||
        b.place.count - a.place.count,
    )
    .slice(0, limit)
    .map(({ place }) => place);
};

const kmBetween = (a, b) => {
  const toRad = (degrees) => (degrees * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
};

export const codesWithin = (areas, center, km) => {
  const near = areas
    .filter((area) => kmBetween(area.label, center) <= km)
    .map((area) => area.code);
  const home = areaAt(areas, center);
  return home && !near.includes(home.code) ? [home.code, ...near] : near;
};

export const circleBounds = ({ lat, lng }, km) => {
  const latSpan = (km / EARTH_RADIUS_KM) * (180 / Math.PI);
  const lngSpan = latSpan / Math.cos((lat * Math.PI) / 180);
  return {
    north: lat + latSpan,
    south: lat - latSpan,
    east: lng + lngSpan,
    west: lng - lngSpan,
  };
};

export const sharedAreas = (areas, prefixes, otherRoutes) => {
  const mine = new Set(areasFor(areas, prefixes).map((area) => area.code));
  const shared = new Map();
  otherRoutes.forEach((route) =>
    areasFor(areas, route.prefixes).forEach(({ code }) => {
      if (mine.has(code)) shared.set(code, [...(shared.get(code) || []), route.name]);
    }),
  );
  return shared;
};

export const areasIn = (areas, box) =>
  areas.filter((area) => overlaps(area.box, box));

export const areaAt = (areas, { lat, lng }) => {
  const point = { type: "Point", coordinates: [lng, lat] };
  return areas.find(
    ({ box, geometry }) =>
      box.west <= lng &&
      lng <= box.east &&
      box.south <= lat &&
      lat <= box.north &&
      booleanIntersects(geometry, point),
  );
};

export const codesTouching = (areas, geometry) => {
  const box = boxOf(geometry.coordinates);
  return areas
    .filter(
      (area) =>
        overlaps(area.box, box) && booleanIntersects(area.geometry, geometry),
    )
    .map((area) => area.code);
};

export const mergePrefixes = (prefixes) => {
  const unique = [...new Set(prefixes)];
  return unique
    .filter(
      (code) =>
        !unique.some((prefix) => prefix !== code && code.startsWith(prefix)),
    )
    .sort();
};

export const toggleArea = (areas, prefixes, code) => {
  const owner = prefixes.find((prefix) => code.startsWith(prefix));
  if (!owner) return mergePrefixes([...prefixes, code]);
  const rest = prefixes.filter((prefix) => prefix !== owner);
  if (owner === code) return rest;
  const siblings = areasFor(areas, [owner])
    .map((area) => area.code)
    .filter((other) => other !== code);
  return mergePrefixes([...rest, ...siblings]);
};

export const boundsOf = (areas) =>
  areas.length
    ? areas
        .map((area) => area.box)
        .reduce((a, b) => ({
          north: Math.max(a.north, b.north),
          south: Math.min(a.south, b.south),
          east: Math.max(a.east, b.east),
          west: Math.min(a.west, b.west),
        }))
    : null;
